import React, { useState } from 'react';
import { Dropdown, SalvBanner, SalvBannerType, SalvButton, SalvTextArea, SalvTextField } from '@salvapp/ui-components';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { Spinner } from '../../feedback/Spinner';
import { parseErrorMessage } from '../../common/errors/helpers';
import { InternalApiFinalRiskLevel } from '../../api-internal';
import { useBulkOverride, useOverrideReasons } from './bulkRiskOverrideActions';
import { formatEnumLabel } from '../../common/helper';

const MAX_COMMENT_LENGTH = 250;

const validationSchema = Yup.object().shape({
  personExternalIds: Yup.string().required('Provide person external IDs separated by commas'),
  reasonHandle: Yup.string().required('Selecting override reason is mandatory'),
  level: Yup.string().required('Selecting risk level is mandatory'),
  comment: Yup.string()
    .max(MAX_COMMENT_LENGTH, `Comment must be at most ${MAX_COMMENT_LENGTH} characters`)
    .required('Comment is required'),
});

export interface BulkRiskOverrideFormValues {
  personExternalIds: string | undefined;
  reasonHandle: string | undefined;
  level: InternalApiFinalRiskLevel | undefined;
  comment: string | undefined;
  feedback: string | undefined;
  feedbackType: SalvBannerType;
}

const initialValues: BulkRiskOverrideFormValues = {
  personExternalIds: undefined,
  reasonHandle: undefined,
  level: undefined,
  comment: undefined,
  feedback: undefined,
  feedbackType: SalvBannerType.SUCCESS,
};

const enum State {
  EDIT,
  FINAL_WARNING,
  LOADING,
}

const BulkRiskOverrideForm = () => {
  const [formStatus, setFormStatus] = useState(State.EDIT);

  const { data: overrideReasons = [] } = useOverrideReasons();
  const { mutateAsync: bulkOverride } = useBulkOverride();

  const reasonOptions: DropdownOption[] = overrideReasons.map((reason) => ({
    label: reason.reason,
    value: reason.handle,
    key: reason.handle,
  }));

  const levelOptions: DropdownOption[] = Object.values(InternalApiFinalRiskLevel).map((level) => ({
    label: formatEnumLabel(level),
    value: level,
    key: level,
  }));

  const submit = async (
    values: BulkRiskOverrideFormValues,
    formikHelpers: FormikHelpers<BulkRiskOverrideFormValues>
  ) => {
    setFormStatus(State.LOADING);
    try {
      await bulkOverride({
        personExternalIds: values
          .personExternalIds!.split(',')
          .map((id) => id.trim())
          .filter(Boolean),
        reasonHandle: values.reasonHandle!,
        level: values.level!,
        comment: values.comment!,
      });
      formikHelpers.setFieldValue(
        'feedback',
        'Started bulk risk override in the background, please check individual persons to verify result'
      );
      formikHelpers.setFieldValue('feedbackType', SalvBannerType.SUCCESS);
    } catch (e) {
      formikHelpers.setFieldValue('feedback', await parseErrorMessage(e));
      formikHelpers.setFieldValue('feedbackType', SalvBannerType.DANGER);
    } finally {
      formikHelpers.setSubmitting(false);
      setFormStatus(State.EDIT);
    }
  };

  const getIdCount = (ids: string | undefined) => {
    if (!ids) {
      return 0;
    }
    return ids.split(',').filter(Boolean).length;
  };

  return (
    <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema} validateOnMount>
      {({
        submitForm,
        values,
        errors,
        isValid,
        isSubmitting,
        setFieldValue,
        touched,
      }: FormikProps<BulkRiskOverrideFormValues>) => {
        return (
          <>
            <div className='m-b-24'>
              <Dropdown
                label='Override Reason'
                name='override-reason'
                onChange={(opt: DropdownOption[]) => setFieldValue('reasonHandle', opt[0].value)}
                options={reasonOptions}
                placeholder='Select override reason'
                size='medium'
              />
            </div>
            <div className='m-b-24'>
              <Dropdown
                label='Risk Level'
                name='risk-level'
                onChange={(opt: DropdownOption[]) => setFieldValue('level', opt[0].value)}
                options={levelOptions}
                placeholder='Select risk level'
                size='medium'
              />
            </div>
            <div className='m-b-24'>
              <SalvTextField
                label='Comment'
                placeholder='Add a comment for the override'
                value={values.comment}
                onChange={(value) => setFieldValue('comment', value)}
                isInvalid={touched.comment && !!errors.comment}
                disabled={formStatus !== State.EDIT}
              />
            </div>
            <div className='m-b-24'>
              <SalvTextArea
                label='Person External IDs (separated by a ",")'
                placeholder='Person external IDs, separated by a ","'
                value={values.personExternalIds}
                autoSizing
                minRows={12}
                onChange={(value) => setFieldValue('personExternalIds', value)}
                isInvalid={touched.personExternalIds && !!errors.personExternalIds}
                disabled={formStatus !== State.EDIT}
              />
            </div>
            {formStatus === State.EDIT && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <SalvButton
                  disabled={isSubmitting || !isValid}
                  onClick={() => setFormStatus(State.FINAL_WARNING)}
                  variant='primary'
                  label='Confirm'
                />
              </div>
            )}
            {formStatus === State.EDIT && values.feedback && (
              <SalvBanner className='m-t-16' message={values.feedback} type={values.feedbackType} />
            )}
            {formStatus === State.LOADING && <Spinner loading={true} />}
            {formStatus === State.FINAL_WARNING && (
              <div>
                <div style={{ textAlign: 'right', paddingBottom: '12px' }}>Confirm risk override?</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <SalvButton onClick={() => setFormStatus(State.EDIT)} variant='muted' label='Return to editing' />
                  <SalvButton
                    disabled={isSubmitting || !isValid}
                    onClick={submitForm}
                    variant='danger-outline'
                    label={`Override ${getIdCount(values.personExternalIds)} persons`}
                  />
                </div>
              </div>
            )}
          </>
        );
      }}
    </Formik>
  );
};

export default BulkRiskOverrideForm;
